import type { Realtime, TokenRequest } from "ably"
import Ably from "ably"
import { useEffect, useEffectEvent, useState } from "react"
import { getAccessToken } from "@/lib/token-manager"

export type RealtimeConnectionStatus =
  | "connecting"
  | "connected"
  | "reconnecting"
  | "disconnected"
  | "failed"

interface UseAblyRealtimeClientOptions {
  userId: string | null
  tokenUrl: string
  requestedChannels?: string[]
}

interface HttpError extends Error {
  status?: number
}

type RealtimeAuthCallback = (
  tokenParams: unknown,
  callback: (errorInfo: string | null, tokenRequest: TokenRequest | null) => void,
) => Promise<void>

const RETRYABLE_STATUS = 503
const MAX_TOKEN_RETRIES = 3

const createHttpError = (status: number): HttpError => {
  const error = new Error(`Failed to fetch realtime token (${status})`) as HttpError
  error.status = status
  return error
}

const wait = async (delayMs: number): Promise<void> => {
  await new Promise(resolve => {
    setTimeout(resolve, delayMs)
  })
}

export const useAblyRealtimeClient = ({
  userId,
  tokenUrl,
  requestedChannels = [],
}: UseAblyRealtimeClientOptions): {
  client: Realtime | null
  isConnected: boolean
  connectionStatus: RealtimeConnectionStatus
} => {
  const [client, setClient] = useState<Realtime | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const [connectionStatus, setConnectionStatus] = useState<RealtimeConnectionStatus>("disconnected")

  const requestedChannelsKey = requestedChannels.slice().sort().join(",")

  const fetchTokenRequest = useEffectEvent(async (): Promise<TokenRequest> => {
    for (let attempt = 0; attempt < MAX_TOKEN_RETRIES; attempt += 1) {
      const accessToken = getAccessToken()

      if (!accessToken) {
        throw new Error("No access token available")
      }

      const url = new URL(tokenUrl)
      requestedChannels.forEach(channelName => {
        url.searchParams.append("channels", channelName)
      })

      const response = await fetch(url.toString(), {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })

      if (response.ok) {
        const payload = (await response.json()) as {
          data: TokenRequest
        }

        return payload.data
      }

      if (response.status === RETRYABLE_STATUS && attempt < MAX_TOKEN_RETRIES - 1) {
        setConnectionStatus("reconnecting")
        await wait(2 ** attempt * 1000)
        continue
      }

      throw createHttpError(response.status)
    }

    throw createHttpError(RETRYABLE_STATUS)
  })

  useEffect(() => {
    if (!userId || !getAccessToken()) {
      setClient((currentClient: Realtime | null) => {
        currentClient?.close()
        return null
      })
      setIsConnected(false)
      setConnectionStatus("disconnected")
      return
    }

    setConnectionStatus("connecting")

    let hasConnected = false
    const scopedChannelsKey = requestedChannelsKey
    const authCallback: RealtimeAuthCallback = async (_tokenParams, callback) => {
      try {
        void scopedChannelsKey
        const tokenRequest = await fetchTokenRequest()
        callback(null, tokenRequest)
      } catch (error) {
        setConnectionStatus("failed")
        console.error("Failed to authorize realtime client", {
          requestedChannelsKey: scopedChannelsKey,
          error: error instanceof Error ? error.message : String(error),
        })
        callback(
          error instanceof Error ? error.message : "Failed to authorize realtime client",
          null,
        )
      }
    }

    const ablyClient = new Ably.Realtime({
      authCallback,
      clientId: userId,
      echoMessages: false,
    })

    setClient(ablyClient)

    const handleConnecting = () => {
      setIsConnected(false)
      setConnectionStatus(hasConnected ? "reconnecting" : "connecting")
    }
    const handleConnected = () => {
      hasConnected = true
      setIsConnected(true)
      setConnectionStatus("connected")
    }
    const handleDisconnected = () => {
      setIsConnected(false)
      setConnectionStatus("disconnected")
    }
    const handleFailed = () => {
      setIsConnected(false)
      setConnectionStatus("failed")
    }
    const handleClosed = () => {
      setIsConnected(false)
      setConnectionStatus("disconnected")
    }

    ablyClient.connection.on("connecting", handleConnecting)
    ablyClient.connection.on("connected", handleConnected)
    ablyClient.connection.on("disconnected", handleDisconnected)
    ablyClient.connection.on("failed", handleFailed)
    ablyClient.connection.on("closed", handleClosed)

    return () => {
      ablyClient.connection.off("connecting", handleConnecting)
      ablyClient.connection.off("connected", handleConnected)
      ablyClient.connection.off("disconnected", handleDisconnected)
      ablyClient.connection.off("failed", handleFailed)
      ablyClient.connection.off("closed", handleClosed)
      ablyClient.close()
      setClient((currentClient: Realtime | null) =>
        currentClient === ablyClient ? null : currentClient,
      )
      setIsConnected(false)
      setConnectionStatus("disconnected")
    }
  }, [userId, requestedChannelsKey])

  return {
    client,
    isConnected,
    connectionStatus,
  }
}
